import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { X, CheckCircle, Car, Baby, Dog, Briefcase, ArrowRight } from "lucide-react";
import { IMAGES } from "./constants";
import { useI18n } from "../../i18n";
import { usePricing } from "../../context/PricingContext";

const CATEGORY_META = {
  vehiculos: { image: IMAGES.carRear1, icon: Car, span: "lg:col-span-3", height: "h-64 lg:h-72" },
  ninos: { image: IMAGES.kids, icon: Baby, span: "lg:col-span-2", height: "h-64 lg:h-72" },
  mascotas: { image: IMAGES.dogs, icon: Dog, span: "lg:col-span-2", height: "h-64" },
  objetos: { image: IMAGES.objects, icon: Briefcase, span: "lg:col-span-3", height: "h-64" },
};

const CategoryCards = ({ selectedCategory, setSelectedCategory }) => {
  const { t } = useI18n();
  const { priceLabelSemantic } = usePricing();
  const keys = Object.keys(CATEGORY_META);
  const meta = selectedCategory ? CATEGORY_META[selectedCategory] : null;
  const catT = selectedCategory ? t(`cats.${selectedCategory}`) : null;

  return (
    <>
      {meta && catT && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B2545]/80 p-4 backdrop-blur-sm"
          onClick={() => setSelectedCategory(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-56 overflow-hidden md:h-64">
              <img
                src={meta.image}
                alt={catT.title}
                className="h-full w-full object-cover object-center"
                loading="eager"
                decoding="async"
              />
              <button
                onClick={() => setSelectedCategory(null)}
                className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#0B2545]/50 text-white backdrop-blur-sm transition-colors hover:bg-[#0B2545]/80"
                aria-label={t("cats.close_aria")}
                data-testid="category-modal-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-7">
              <h3 className="mb-1 text-2xl font-extrabold tracking-tight text-[#0B2545]">{catT.title}</h3>
              <p className="mb-6 text-slate-500">{catT.subtitle}</p>

              <ul className="mb-7 space-y-4">
                {(catT.bullets || []).map((bullet, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#1B84FF]" />
                    <span className="text-slate-700">{bullet}</span>
                  </li>
                ))}
              </ul>

              <Link to="/register" onClick={() => setSelectedCategory(null)}>
                <Button
                  className="h-auto w-full whitespace-normal rounded-full bg-[#1B84FF] py-5 text-base font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-[#0F6FDB]"
                  data-testid="category-modal-cta"
                >
                  {priceLabelSemantic(6) ? t("cats.activate_cta", { price: priceLabelSemantic(6) }) : t("cats.activate_cta_nc")}
                </Button>
              </Link>
              <p className="mt-3 text-center text-sm text-slate-400">{t("cats.active_note")}</p>
            </div>
          </div>
        </div>
      )}

      <section id="usos" className="bg-[#F8FAFC] py-20 md:py-28" data-testid="use-cases-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-2xl">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#1B84FF]">
              {t("cats.eyebrow")}
            </p>
            <h2 className="mb-4 text-3xl font-extrabold tracking-tight text-[#0B2545] sm:text-4xl lg:text-5xl">
              {t("cats.title")}
            </h2>
            <p className="text-base text-slate-600 md:text-lg">{t("cats.subtitle")}</p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-5">
            {keys.map((key) => {
              const { image, icon: Icon, span, height } = CATEGORY_META[key];
              const c = t(`cats.${key}`);
              return (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white text-left shadow-[0_8px_30px_rgba(11,37,69,0.05)] transition-shadow duration-300 hover:shadow-[0_20px_44px_rgba(27,132,255,0.16)] focus-visible:ring-2 focus-visible:ring-[#1B84FF] focus-visible:ring-offset-2 ${span}`}
                  data-testid={`category-card-${key}`}
                >
                  <div className={`relative overflow-hidden ${height}`}>
                    <img
                      src={image}
                      alt={c.title}
                      className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545]/85 via-[#0B2545]/25 to-transparent" />
                    <span className="absolute left-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 shadow-md">
                      <Icon className="h-5 w-5 text-[#0B2545]" />
                    </span>
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <h3 className="text-xl font-bold text-white">{c.title}</h3>
                      <p className="mt-1 line-clamp-2 text-sm text-slate-200">{c.subtitle}</p>
                      <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#9FD8FF] transition-transform group-hover:translate-x-1">
                        {t("cats.see_details")}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default CategoryCards;
